import React, { useCallback, useEffect, useMemo } from "react";

import { useRouterWithTypedParams } from "../../../../hooks/useRouterWithTypedParams";
import { PageHeader } from "../../../PageHeader";
import { ConfigurationContainer } from "../../../shared/ConfigurationContainer";
import { LogoUploadSection } from "../../../shared/LogoUploadSection";
import { useApi } from "../../../useApi";

import { OrganizationEditForm } from "./OrganizationEditForm";
import { useOrganizationLoad } from "./useOrganizationLoad";
import { useOrganizationLogoLoad } from "./useOrganizationLogoLoad";

export const OrganizationConfigPage = () => {
    const { match } = useRouterWithTypedParams<{ orgId?: number }>();
    const organizationId = match.params.orgId;
    const { orgApi } = useApi();

    const {
        loadOrganization,
        loading: isLoadingOrganization,
        organization,
    } = useOrganizationLoad(organizationId);

    const {
        loadLogo,
        loading: isLoadingLogo,
        logo,
    } = useOrganizationLogoLoad(organizationId);

    const loadOrganizationData = useCallback(() => {
        loadOrganization();
        loadLogo();
    }, [loadOrganization, loadLogo]);

    const uploadLogoMethod = useMemo(() => {
        if (!organizationId) return;
        return (data: FormData) => orgApi.postLogo(organizationId, data);
    }, [orgApi, organizationId]);

    const deleteLogoMethod = useMemo(() => {
        if (!organizationId) return;
        return () => orgApi.deleteLogo(organizationId);
    }, [orgApi, organizationId]);

    useEffect(() => {
        loadOrganizationData();
    }, [loadOrganizationData]);

    const loadingData = isLoadingOrganization || isLoadingLogo;

    return (
        <>
            <PageHeader isLoading={loadingData}>
                Organization Configuration: {organization && organization.name}
            </PageHeader>

            {organization && (
                <ConfigurationContainer>
                    <OrganizationEditForm
                        organization={organization}
                        onEditSuccess={loadOrganization}
                        disabled={loadingData}
                    />

                    {uploadLogoMethod && deleteLogoMethod && (
                        <LogoUploadSection
                            label="Logo"
                            onUploadSuccess={loadLogo}
                            disabled={loadingData}
                            loadedLogo={logo}
                            loadingLogo={isLoadingLogo}
                            uploadMethod={uploadLogoMethod}
                            deleteMethod={deleteLogoMethod}
                        />
                    )}
                </ConfigurationContainer>
            )}
        </>
    );
};
